import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, Pin, Trash2, Megaphone, X } from "lucide-react";
import { toast } from "sonner";

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });

  const load = async () => {
    try { const { data } = await api.get("/announcements"); setRows(data); }
    catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/announcements", form);
      toast.success("Posted"); setOpen(false); setForm({ title: "", body: "", pinned: false }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Failed")); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`); toast.success("Deleted"); load();
  };

  const canPost = user.role === "company_admin";

  return (
    <div>
      <PageHeader eyebrow="Broadcast" title="Announcements." testid="page-announcements"
        description={canPost ? "Post company updates. Pinned ones stay at the top." : "Latest updates from your HR team."}
        actions={canPost && (
          <button onClick={() => setOpen(true)} data-testid="btn-new-announcement"
            className="inline-flex items-center gap-2 bg-neutral-950 text-[#D4B87D] px-4 py-2.5 font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">
            <Plus className="w-4 h-4" /> New announcement
          </button>
        )}
      />
      <div className="p-8 space-y-4" data-testid="announcements-list">
        {loading && <div className="font-mono text-xs text-neutral-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="border border-dashed border-[#C9A961] p-10 text-center font-mono text-sm text-neutral-500">
            <Megaphone className="w-8 h-8 mx-auto mb-3 text-[#8C6F1E]" /> No announcements yet.
          </div>
        )}
        {rows.map((a) => (
          <div key={a.id} data-testid={`ann-${a.id}`} className={`border p-6 bg-white transition-colors ${a.pinned ? "border-[#C9A961] shadow-[0_0_0_1px_rgba(201,169,97,0.35)]" : "border-neutral-200"}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {a.pinned && <Pin className="w-3.5 h-3.5 text-[#8C6F1E]" />}
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">{a.created_by} · {a.created_at?.slice(0, 10)}</div>
                </div>
                <h3 className="font-serif font-bold text-2xl tracking-tight mb-2">{a.title}</h3>
                <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap">{a.body}</p>
              </div>
              {canPost && (
                <button onClick={() => remove(a.id)} data-testid={`del-ann-${a.id}`} className="text-neutral-400 hover:text-[#B33A1F]"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-neutral-950/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-[#C9A961] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8C6F1E]">Broadcast</div><div className="font-serif font-bold text-2xl tracking-tight">New announcement</div></div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <input required value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="ann-title" placeholder="Title"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-serif text-lg" />
              <textarea required rows={6} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} data-testid="ann-body" placeholder="Message body…"
                className="w-full px-3 py-2.5 border border-neutral-300 focus:border-[#C9A961] outline-none font-mono text-sm" />
              <label className="flex items-center gap-2 text-sm font-mono cursor-pointer">
                <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({...form, pinned: e.target.checked})} data-testid="ann-pin" /> Pin to top
              </label>
              <button type="submit" data-testid="btn-post-announcement"
                className="w-full px-5 py-3 bg-neutral-950 text-[#D4B87D] font-mono text-sm hover:bg-[#C9A961] hover:text-neutral-950 border border-neutral-950 hover:border-[#C9A961] transition-colors">Post</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
